import type {InputHTMLAttributes, ReactNode} from 'react';

import {CloseCircleFilled, ExclamationCircleFilled} from '@ant-design/icons';
import {forwardRef, useId, useState} from 'react';

import {cn} from '../../helpers/cn';

export type InputSize = 'sm' | 'md' | 'lg';
export type InputStatus = 'error' | 'warning';

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> & {
  size?: InputSize;
  status?: InputStatus;
  disabled?: boolean;
  label?: ReactNode;
  required?: boolean;
  help?: ReactNode;
  className?: string;
  wrapperClassName?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  onSuffixClick?: () => void;
  allowClear?: boolean;
  onClear?: () => void;
  inputClassName?: string;
  placeHolder?: string;
  rightIcon?: string;
  leftIcon?: string;
  leftIconClick?: () => void;
  title?: ReactNode;
  error?: string;
  placeholderAlign?: 'rightPlaceholder' | 'leftPlaceholder';
  width?: string;
  height?: string;
  borderColor?: string;
};

const sizeClasses: Record<InputSize, string> = {
  sm: 'min-h-8 px-[11px] text-[12px] leading-[18px]',
  md: 'min-h-10 px-[11px] text-[14px] leading-[22px]',
  lg: 'min-h-12 px-[11px] text-[14px] leading-[24px]'
};

function statusBorderClasses(status?: InputStatus, disabled?: boolean) {
  if (disabled) return 'border-disabled-border bg-disabled-surface';
  if (status === 'error') return 'border-destructive bg-white hover:border-destructive';
  if (status === 'warning') return 'border-yellow bg-white hover:border-yellow';
  return 'border-disabled-border bg-white hover:border-orange';
}

function statusFocusClasses(status?: InputStatus, disabled?: boolean) {
  if (disabled) return '';
  if (status === 'error') {
    return 'focus-within:border-destructive focus-within:shadow-[0_0_0_2px] focus-within:shadow-destructive-surface';
  }
  if (status === 'warning') {
    return 'focus-within:border-yellow focus-within:shadow-[0_0_0_2px] focus-within:shadow-yellow-soft';
  }
  return 'focus-within:border-orange focus-within:shadow-[0_0_0_2px] focus-within:shadow-brand-surface';
}

function InputFieldShell({
  id,
  label,
  required,
  help,
  showHelp,
  wrapperClassName,
  children
}: {
  id?: string;
  label?: ReactNode;
  required?: boolean;
  help?: ReactNode;
  showHelp: boolean;
  wrapperClassName?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('flex w-full flex-col gap-1.5', wrapperClassName)}>
      {label ? (
        <label htmlFor={id} className="text-[14px] font-medium leading-[21px] text-gray-08">
          {label}
          {required ? <span className="text-destructive">*</span> : null}
        </label>
      ) : null}
      {children}
      {showHelp && help ? (
        <div className="flex items-center gap-1.5 px-2 text-[10px] leading-4 text-destructive">
          <i className="icon-Info-Outlined text-[16px]" aria-hidden />
          <span>{help}</span>
        </div>
      ) : null}
    </div>
  );
}

function StatusIcon({status}: {status?: InputStatus}) {
  if (status === 'error') {
    return <ExclamationCircleFilled aria-hidden className="text-[14px] text-destructive" />;
  }
  if (status === 'warning') {
    return <ExclamationCircleFilled aria-hidden className="text-[14px] text-yellow" />;
  }
  return null;
}

function resolvePrefix(prefix?: ReactNode, rightIcon?: string) {
  if (prefix != null) return prefix;
  if (rightIcon) return <i className={rightIcon} aria-hidden style={{fontSize: 20}} />;
  return null;
}

function resolveSuffix(
  suffix: ReactNode | undefined,
  leftIcon: string | undefined,
  status: InputStatus | undefined,
  focused: boolean,
  allowClear: boolean | undefined,
  hasValue: boolean,
  onClear?: () => void
) {
  if (allowClear && hasValue && !focused) {
    return (
      <button
        type="button"
        tabIndex={-1}
        aria-label="پاک کردن"
        className="inline-flex cursor-pointer items-center border-0 bg-transparent p-0 text-gray-06 hover:text-gray-08"
        onClick={onClear}
      >
        <CloseCircleFilled aria-hidden className="text-[14px]" />
      </button>
    );
  }

  if (status && !focused) {
    return <StatusIcon status={status} />;
  }

  if (suffix != null) return suffix;

  if (leftIcon) {
    return <i className={leftIcon} aria-hidden style={{fontSize: 20}} />;
  }

  return null;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    size = 'lg',
    status,
    disabled,
    label,
    required,
    help,
    className,
    wrapperClassName,
    inputClassName,
    prefix,
    suffix,
    onSuffixClick,
    allowClear,
    onClear,
    value,
    defaultValue,
    onChange,
    onFocus,
    onBlur,
    id,
    placeHolder,
    placeholder,
    rightIcon,
    leftIcon,
    leftIconClick,
    title,
    error,
    placeholderAlign,
    width,
    height,
    borderColor,
    style,
    type = 'text',
    ...props
  },
  ref
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const [focused, setFocused] = useState(false);
  const resolvedLabel = label ?? title;
  const resolvedPlaceholder = placeholder ?? placeHolder;
  const resolvedStatus = status ?? (error ? 'error' : undefined);
  const resolvedHelp = help ?? error;
  const showHelp = Boolean(required && resolvedHelp);
  const wrapperStyle = {width, height, borderColor, ...style};
  const prefixNode = resolvePrefix(prefix, rightIcon);
  const hasValue = value != null && String(value).length > 0;
  const suffixNode = resolveSuffix(
    suffix,
    leftIcon,
    resolvedStatus,
    focused,
    allowClear,
    hasValue,
    onClear
  );
  const suffixClick = onSuffixClick ?? leftIconClick;

  const placeholderAlignClass =
    placeholderAlign === 'rightPlaceholder'
      ? 'placeholder:text-right'
      : placeholderAlign === 'leftPlaceholder'
        ? 'placeholder:text-left'
        : undefined;

  return (
    <InputFieldShell
      id={inputId}
      label={resolvedLabel}
      required={required}
      help={resolvedHelp}
      showHelp={showHelp}
      wrapperClassName={wrapperClassName}
    >
      <span
        className={cn(
          'inline-flex w-full items-center gap-2 rounded-[6px] border transition-[border-color,box-shadow]',
          sizeClasses[size],
          statusBorderClasses(resolvedStatus, disabled),
          statusFocusClasses(resolvedStatus, disabled),
          disabled && 'opacity-50',
          className
        )}
        style={wrapperStyle}
      >
        {prefixNode ? (
          <span className="inline-flex shrink-0 items-center text-gray-08">{prefixNode}</span>
        ) : null}
        <input
          {...props}
          ref={ref}
          id={inputId}
          type={type}
          disabled={disabled}
          value={value}
          defaultValue={defaultValue}
          placeholder={resolvedPlaceholder}
          aria-invalid={resolvedStatus === 'error' || undefined}
          className={cn(
            'min-w-0 flex-1 border-0 bg-transparent font-iransans font-medium text-gray-08 outline-none placeholder:font-normal placeholder:text-gray-06 disabled:cursor-not-allowed disabled:text-disabled-fg',
            placeholderAlignClass,
            inputClassName
          )}
          onChange={onChange}
          onFocus={(event) => {
            setFocused(true);
            onFocus?.(event);
          }}
          onBlur={(event) => {
            setFocused(false);
            onBlur?.(event);
          }}
        />
        {suffixNode ? (
          <span
            className={cn(
              'inline-flex shrink-0 items-center text-gray-08',
              suffixClick && 'cursor-pointer'
            )}
            onClick={suffixClick}
            role={suffixClick ? 'button' : undefined}
          >
            {suffixNode}
          </span>
        ) : null}
      </span>
    </InputFieldShell>
  );
});

Input.displayName = 'Input';
